










































import { Component, Vue, Watch } from "vue-property-decorator";
import { CrudService } from "../core/services/crudService";
import { Settings, SettingsService } from "../core/services/configService";
import { CustomerHelper } from "../crm/helpers/customerHelper";
import { ProgramPackTexts } from "../bookings/programPackTexts";
import { GuidelineApi } from "../components/guidelines/guidelineApi";
import { TreatmentApi } from "../components/treatments/treatmentApi";
import ProgramPage from "../components/programs/ProgramPage.vue";
import MyProgramPackPage from "../bookings/myProgramPackPage.vue";
import GuidelinePage from "../guidelines/guidelinePage.vue";
import TreatmentPage from "../treatments/treatmentPage.vue";
import ProgramPackHead from "../components/programs/ProgramPackHead.vue";
import CzScroller from "../components/cz/CzScroller.vue";
import CzTitle from "../components/cz/CzTitle.vue";
import CzLink from "../components/cz/CzLink.vue";
import CzPills from "../components/cz/CzPills.vue";
import CzTabBar from "../components/cz/CzTabBar.vue";
import CzNotice from "../components/cz/CzNotice.vue";

Component.registerHooks(["beforeRouteEnter"]);

// Steps of booking or paying are never where Back should take the patient.
const FlowPages = ["/my-plan", "/bookings", "/checkout", "/cz/", "/customer-programs/", "/programs/"];

const PendingPayment = 1;
const Active = 2;

// Everything prescribed in one page: the programs in progress one at a time, every guideline together wherever it
// came from, and the treatment in its own tab. Each part is the page the patient already knew, embedded.
@Component({
  components: { ProgramPage, MyProgramPackPage, GuidelinePage, TreatmentPage, ProgramPackHead, CzScroller, CzTitle, CzLink, CzPills, CzTabBar, CzNotice },
})
export default class MyPlanPage extends Vue {
  programs: any[] = [];
  routines: any[] = [];
  hasTreatment: boolean = false;
  loaded: boolean = false;
  scrolls: { [part: string]: number } = {};
  direction: string = "right";
  cameFrom: string = "";
  pickedHere: boolean = false;
  toRoutines: boolean = false;
  guidelineFocused: boolean = false;
  failed: boolean = false;

  // Back goes where the patient came from (or the "back" of a link that brought them).
  beforeRouteEnter(to: any, from: any, next: any) {
    next((vm: any) => {
      if (from.name && !FlowPages.some((p) => from.path.startsWith(p))) vm.cameFrom = from.fullPath;
    });
  }

  get backTo(): string {
    return (this.$route.query.back as string) || this.cameFrom || "/bookingtypes";
  }

  get backLabel(): string {
    const to = this.backTo;
    if (to.startsWith("/today")) return this.t("Today");
    if (to.startsWith("/bookingtypes")) return this.t("Reserve");
    return this.t("Back");
  }

  // Coming back from a session, or arriving at one guideline from elsewhere, lands on "Your guidelines".
  @Watch("focused")
  onFocused(now: boolean, before: boolean) {
    if (before && !now) this.scrollToRoutines();
  }

  // The guideline on screen says again whether it is focused; a removed one never leaves the page hidden.
  @Watch("routine.id")
  onRoutineShown() {
    this.guidelineFocused = false;
  }

  @Watch("$route.query.routine")
  onRoutine() {
    if (!this.pickedHere && !this.focused) this.scrollToRoutines();
    this.pickedHere = false;
  }

  // The program above loads on its own: once it has, the guidelines are where they will stay.
  onProgramLoaded() {
    if (this.toRoutines) this.scrollToRoutines();
    this.toRoutines = false;
  }

  scrollToRoutines() {
    this.$nextTick(() => {
      const title = this.$refs.routines as Vue | undefined;
      if (!title) return;
      window.scrollTo(0, title.$el.getBoundingClientRect().top + window.scrollY - 140);
    });
  }

  get current(): any[] {
    return this.programs.filter((e: any) => e.status === PendingPayment || e.status === Active);
  }

  // The finished ones go after the ones in progress, so the patient keeps them without another page.
  get shown(): any[] {
    return this.current.concat(this.programs.filter((e: any) => this.current.indexOf(e) < 0));
  }

  get guidelineRows(): { [id: number]: any } {
    const byId: { [id: number]: any } = {};
    this.routines.forEach((r: any) => (byId[r.id] = r));
    return byId;
  }

  get program(): any {
    const asked = Number(this.$route.query.program);
    return this.shown.find((e: any) => e.id === asked) || this.shown[0] || null;
  }

  get routine(): any {
    // A link to a guideline no longer in the list (ended, closed) still opens that one, never another.
    const asked = Number(this.$route.query.routine);
    if (asked) return this.routines.find((r: any) => r.id === asked) || { id: asked, name: "" };
    return this.routines[0] || null;
  }

  get routinePills(): any[] {
    return this.routines.map((r: any) => ({ value: r.id, label: r.name, note: this.origin(r) }));
  }

  // A session, or what to send, takes the whole page until the patient goes back to the guideline.
  get focused(): boolean {
    return this.guidelineFocused && !!this.routine;
  }

  get parts(): any[] {
    const parts: any[] = [];
    if (this.programs.length || this.routines.length || this.$route.query.routine) parts.push({ value: "plan", label: this.t("Plan"), icon: "program" });
    if (this.hasTreatment) parts.push({ value: "treatment", label: this.t("Treatment"), icon: "treatment" });
    return parts;
  }

  get part(): string {
    const asked = this.$route.query.part as string;
    const found = this.parts.find((p: any) => p.value === asked) || this.parts[0];
    return found ? found.value : "";
  }

  get title(): string {
    if (this.focused) return "";
    return this.part === "treatment" ? this.t("My treatment") : this.t("My plan");
  }

  get emptyText(): string {
    return this.loaded && !this.failed && !this.parts.length ? this.t("You have no plan in progress") : "";
  }

  origin(r: any): string {
    const owner = r.customerProgramPackCardId
      ? this.programs.find((e: any) => e.columns.some((c: any) => c.cards.some((k: any) => k.id === r.customerProgramPackCardId)))
      : null;
    if (owner) return this.t("From the program {0}").replace("{0}", owner.name);
    if (!r.startDate) return "";
    const date = ProgramPackTexts.date(r.startDate, false);
    return r.fromBooking ? this.t("From your appointment on {0}").replace("{0}", date) : this.t("Since {0}").replace("{0}", date);
  }

  pickRoutine(id: number) {
    this.pickedHere = true;
    this.$router.replace({ path: "/my-plan", query: Object.assign({}, this.$route.query, { routine: String(id) }) });
  }

  // Each tab comes back where the patient left it.
  async pickPart(value: string) {
    this.scrolls[this.part] = window.scrollY;
    this.direction = this.parts.findIndex((p: any) => p.value === value) > this.parts.findIndex((p: any) => p.value === this.part) ? "right" : "left";
    await this.$router.replace({ path: "/my-plan", query: Object.assign({}, this.$route.query, { part: value }) });
    new SettingsService().setPageTitle(this.title);
    this.$nextTick(() => window.scrollTo(0, this.scrolls[value] || 0));
  }

  t(key: string): string {
    return this.$t(key).toString();
  }

  mounted() {
    return this.load();
  }

  // A failed read never looks like an empty plan: it says so and offers to retry. The treatment tab stays so its own
  // retry is reachable.
  async load() {
    const [programs, routines, treatment] = await Promise.all([
      new CrudService().httpGet(`${Settings.HostName}/api/publicprogrampacks/customer/${CustomerHelper.getStoredCustomer()}`).catch(() => null),
      GuidelineApi.list().catch(() => null),
      TreatmentApi.today().catch(() => null),
    ]);
    const programsOk = !!programs && programs.ok === true;
    const routinesOk = !!routines && routines.ok === true;
    const treatmentOk = !!treatment && treatment.ok === true;
    this.failed = !programsOk || !routinesOk;
    this.programs = programsOk ? programs.data : this.programs;
    this.routines = routinesOk ? routines.data : this.routines;
    this.hasTreatment = !treatmentOk || treatment.model.rows.length > 0 || treatment.model.hasTreatment === true;
    this.loaded = true;
    new SettingsService().setPageTitle(this.title);
    if (this.$route.query.routine && !this.focused) {
      this.toRoutines = !!this.program;
      this.scrollToRoutines();
    }
  }

}
